import { useState, useMemo, useEffect, useRef } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { countries } from '../../constants/countries';
import LanguageStore from '../../store/LanguageStore';
import { observer } from 'mobx-react-lite';
import './country-guess-game.scss';

const CountryGuessGame = observer(({ countryCode, videoCountries, onClose, onCorrectAnswer, onIncorrectAnswer, onContinue, myScore, opponentScore, myErrors, opponentErrors }) => {
    const { language } = LanguageStore;
    const t = LanguageStore.t;
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const incorrectAnswerReported = useRef(false);

    // Сбрасываем состояние при изменении страны
    useEffect(() => {
        setSelectedAnswer(null);
        setShowFeedback(false);
        incorrectAnswerReported.current = false;
    }, [countryCode]);

    // Получаем название страны по коду
    const getCountryName = (code) => {
        const country = countries.find(c => c.code === code);
        return country ? country[language] || country.gb : code;
    };

    // Генерируем варианты ответов (5-6 стран из списка стран видео)
    // Мемоизируем, чтобы варианты не пересмешивались при каждом рендере
    const options = useMemo(() => {
        const uniqueCountries = [...new Set(videoCountries.map(c => c.code))];
        const optionsList = [countryCode];
        
        while (optionsList.length < 6 && optionsList.length < uniqueCountries.length + 1) {
            const randomCountry = uniqueCountries[Math.floor(Math.random() * uniqueCountries.length)];
            if (!optionsList.includes(randomCountry)) {
                optionsList.push(randomCountry);
            }
        }
        
        // Перемешиваем варианты с фиксированным seed для стабильности
        // Используем более стабильный seed на основе countryCode
        const seed = countryCode.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const sortedOptions = optionsList.sort((a, b) => {
            const hashA = a.split('').reduce((acc, char) => acc + char.charCodeAt(0), seed);
            const hashB = b.split('').reduce((acc, char) => acc + char.charCodeAt(0), seed);
            return (hashA % 100) - (hashB % 100);
        });
        
        // Возвращаем стабильный порядок
        return sortedOptions;
    }, [countryCode, videoCountries]);

    const isCorrect = selectedAnswer === countryCode;

    const handleOptionClick = (option) => {
        if (selectedAnswer) return; // Уже выбран ответ
        
        setSelectedAnswer(option);
        setShowFeedback(true);
        
        if (option === countryCode && onCorrectAnswer) {
            // Задержка перед вызовом callback для показа результата
            setTimeout(() => {
                onCorrectAnswer();
            }, 750);
        } else {
            // При неправильном ответе вызываем callback для увеличения счетчика ошибок
            // Но только один раз для этой попытки
            if (onIncorrectAnswer && !incorrectAnswerReported.current) {
                incorrectAnswerReported.current = true;
                onIncorrectAnswer();
            }
            // При неправильном ответе автоматически переходим к следующей стране после задержки
            if (onContinue) {
                setTimeout(() => {
                    handleContinue();
                }, 1500); // Даем время увидеть правильный ответ
            }
        }
    };

    const handleContinue = () => {
        // Сбрасываем состояние перед переходом к следующей стране
        setSelectedAnswer(null);
        setShowFeedback(false);
        incorrectAnswerReported.current = false;
        
        // При продолжении после ошибки НЕ увеличиваем счет
        // Используем отдельный callback onContinue, если он есть, иначе просто закрываем
        if (onContinue) {
            onContinue();
        } else if (onClose) {
            onClose();
        }
    };

    return (
        <div className="country-guess-game">
            <div className="country-guess-game__modal">
                <div className="country-guess-game__content">
                    <button 
                        className="country-guess-game__close-button"
                        onClick={onClose}
                        aria-label={t('close')}
                    >
                        ×
                    </button>
                    <h2 className="country-guess-game__title">{t('guessCountryTitle')}</h2>
                    {myScore !== undefined && (
                        <div className="country-guess-game__scores">
                            <div className="country-guess-game__score">
                                <span className="country-guess-game__score-label">{t('yourScore')}</span>
                                <span className="country-guess-game__score-value">
                                    {myScore}
                                    {myErrors !== undefined && myErrors > 0 && (
                                        <span className="country-guess-game__score-errors">/{myErrors}</span>
                                    )}
                                </span>
                            </div>
                            {opponentScore !== undefined && (
                                <div className="country-guess-game__score">
                                    <span className="country-guess-game__score-label">{t('opponentScore')}</span>
                                    <span className="country-guess-game__score-value">
                                        {opponentScore}
                                        {opponentErrors !== undefined && opponentErrors > 0 && (
                                            <span className="country-guess-game__score-errors">/{opponentErrors}</span>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="country-guess-game__flag-container">
                        <ReactCountryFlag
                            countryCode={countryCode}
                            svg
                            className="country-guess-game__flag"
                            style={{
                                width: '120px',
                                height: 'auto'
                            }}
                        />
                    </div>
                    <div className="country-guess-game__options">
                        {options.map((option) => {
                            const isSelected = selectedAnswer === option;
                            const isCorrectOption = option === countryCode;
                            
                            return (
                                <button
                                    key={option}
                                    className={`country-guess-game__option ${
                                        isSelected 
                                            ? (isCorrectOption ? 'country-guess-game__option--correct' : 'country-guess-game__option--incorrect')
                                            : ''
                                    }`}
                                    onClick={() => handleOptionClick(option)}
                                    disabled={!!selectedAnswer}
                                >
                                    {getCountryName(option)}
                                    {isSelected && isCorrectOption && (
                                        <span className="country-guess-game__feedback country-guess-game__feedback--correct">✓</span>
                                    )}
                                    {isSelected && !isCorrectOption && (
                                        <span className="country-guess-game__feedback country-guess-game__feedback--incorrect">✗</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {showFeedback && (
                        <div className={`country-guess-game__result ${
                            isCorrect ? 'country-guess-game__result--correct' : 'country-guess-game__result--incorrect'
                        }`}>
                            {isCorrect ? t('guessCountryCorrect') : t('guessCountryIncorrect', getCountryName(countryCode))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default CountryGuessGame;

